import Decimal from 'decimal.js';
import { compact, groupBy, orderBy, uniqBy } from 'lodash';
import { Dispatch } from 'react';

import { EntityType } from '@/modules/entities/types/EntityType';
import {
  EstateWaterfall_EdgeFragment,
  EstateWaterfall_TransferLogItemFragment,
} from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import {
  EstateWaterfallAction,
  EstateWaterfallGraphNodeAttributes,
  GraphNode,
  GraphNodeData,
} from '@/modules/estateWaterfall/types';
import {
  isGiftTaxNode,
  isTaxNode,
} from '@/modules/estateWaterfall/waterfallGraph/utils';
import {
  EstateWaterfallEdgeKind,
  EstateWaterfallTransferKind,
} from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';

import {
  CardConfig,
  createCltFundingCard,
  createCltIncomeCard,
  createCltRemainderCard,
  createCrtFundingCard,
  createCrtIncomeCard,
  createCrtRemainderCard,
  createDispositionCard,
  createGratFundingCard,
  createGratIncomeCard,
  createGratRemainderCard,
  createHypotheticalSaleLoanCard,
  createLiabilityCard,
  createPouroverCard,
  createQprtRemainderCard,
  createTaxCard,
  createTransferCard,
} from './';
import { createScheduledDistributionCard } from './ScheduledDistributionCard';

const UNKNOWN_CAUSE_ID = 'unknown';

interface CreateEdgeCardsParams {
  edge: EstateWaterfall_EdgeFragment | undefined;
  source: string;
  target: string;
  sourceEntityName: string;
  targetEntityName: string;
  sourceIsTestamentaryEntity: boolean;
  sourceIsClientProfile: boolean;
  entityType: EntityType | null;
  label?: string;
  value?: string;
  decedentName?: string;
  deathYear?: number;
  sourceNodeAttributes: EstateWaterfallGraphNodeAttributes<
    GraphNode,
    GraphNodeData
  >;
  targetNodeAttributes: EstateWaterfallGraphNodeAttributes<
    GraphNode,
    GraphNodeData
  >;
  dispatch: Dispatch<EstateWaterfallAction>;
  waterfallTransfers: EstateWaterfall_TransferLogItemFragment[];
  waterfallEdgeFragments: EstateWaterfall_EdgeFragment[];
  firstGrantorDeathYear?: number | null;
  secondGrantorDeathYear?: number | null;
  firstGrantorId?: string;
  secondGrantorId?: string;
}

/**
 * Creates cards for an edge based on its properties
 *
 * This function determines which cards should be created for a given edge
 * based on its type and properties.
 */
export function createEdgeCards({
  edge,
  source,
  target,
  sourceEntityName,
  targetEntityName,
  sourceIsTestamentaryEntity,
  sourceIsClientProfile,
  entityType,
  label,
  value,
  decedentName,
  deathYear: _deathYear,
  sourceNodeAttributes,
  targetNodeAttributes,
  dispatch,
  waterfallTransfers,
  waterfallEdgeFragments,
  firstGrantorDeathYear,
  secondGrantorDeathYear,
  firstGrantorId,
  secondGrantorId,
}: CreateEdgeCardsParams): CardConfig[] {
  const cards: CardConfig[] = [];

  const scheduledPayments = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) =>
        item.kind === EstateWaterfallTransferKind.BeneficiaryPayment &&
        item.associatedObject?.__typename === 'ScheduledDistribution'
    ),
    (item) => item.associatedObject?.id
  );
  Object.values(scheduledPayments).forEach((payments) => {
    const card = createScheduledDistributionCard({
      source,
      target,
      sourceEntityName,
      targetEntityName,
      payments,
      dispatch,
    });
    if (card) cards.push(card);
  });

  // Add cards for transfers
  const transfersByCause = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (l) =>
        l.kind === EstateWaterfallTransferKind.HypotheticalValue ||
        l.kind === EstateWaterfallTransferKind.HypotheticalOwnershipStake
    ),
    (t) => t.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(transfersByCause).forEach(([_causeId, transfers]) => {
    // If there are recurring transfers, create a single card for the most recent transfer
    const orderedTransfers = orderBy(transfers, (t) => t.year, 'asc');
    const mostRecentTransfer = orderedTransfers[0];

    if (!mostRecentTransfer) {
      return;
    }

    cards.push(
      createTransferCard({
        sourceEntityName,
        targetEntityName,
        transfer: mostRecentTransfer,
        firstGrantorDeathYear,
        secondGrantorDeathYear,
        firstGrantorId,
        secondGrantorId,
      })
    );
  });

  // Add cards for life insurance
  const lifeInsuranceByCause = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (l) =>
        l.kind === EstateWaterfallTransferKind.HypotheticalInsurancePolicy ||
        l.kind ===
          EstateWaterfallTransferKind.HypotheticalInsurancePolicyFunding
    ),
    (t) => t.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(lifeInsuranceByCause).forEach(([_causeId, transfers]) => {
    // If there are recurring transfers, create a single card for the most recent transfer
    const orderedTransfers = orderBy(transfers, (t) => t.year, 'asc');
    const mostRecentTransfer = orderedTransfers[0];

    if (!mostRecentTransfer) {
      return;
    }

    cards.push(
      createTransferCard({
        sourceEntityName,
        targetEntityName,
        transfer: mostRecentTransfer,
        firstGrantorDeathYear,
        secondGrantorDeathYear,
        firstGrantorId,
        secondGrantorId,
      })
    );
  });

  // Add card for taxes
  if (isTaxNode(target) || isGiftTaxNode(target)) {
    cards.push(
      createTaxCard({
        source,
        target,
        sourceEntityName,
        decedentName,
        label,
        value,
        dispatch,
        isGiftTax: isGiftTaxNode(target),
      })
    );
  }

  // Add cards for pourover wills
  const pouroverTransfersByCause = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (l) => l.kind === EstateWaterfallTransferKind.PouroverWill
    ),
    (t) => t.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(pouroverTransfersByCause).forEach(([_causeId, items]) => {
    if (items.length > 0) {
      const pouroverCard = createPouroverCard({
        associatedPouroverDispositions: items,
        sourceEntityName,
        targetEntityName,
        decedentName,
      });
      cards.push(pouroverCard);
    }
  });

  // Add card for hypothetical sale/loan
  const edgeContainsHypotheticalSaleLoan =
    edge?.associatedTransferLogItems?.find(
      (item) => item.kind === EstateWaterfallTransferKind.HypotheticalSaleLoan
    );

  if (edgeContainsHypotheticalSaleLoan && edge) {
    const associatedRepayments = edge.associatedTransferLogItems?.filter(
      (item) =>
        item.kind === EstateWaterfallTransferKind.LiabilityPayment ||
        item.kind === EstateWaterfallTransferKind.LiabilityPaymentOnDeath
    );

    edge.associatedTransferLogItems?.forEach((item) => {
      if (item.kind === EstateWaterfallTransferKind.HypotheticalSaleLoan) {
        const repaymentsForLoan = associatedRepayments?.filter(
          (r) => r.associatedObjectID === item.associatedObjectID
        );

        cards.push(
          createHypotheticalSaleLoanCard({
            edge,
            sourceEntityName,
            targetEntityName,
            transferLogItem: item,
            totalPayments: sumDecimalJS(
              repaymentsForLoan?.map(
                (r) => r.finalTransferAmount ?? new Decimal(0)
              ) ?? []
            ),
          })
        );
      }
    });
  }

  // Add card for dispositive provisions
  if (edge?.kind === EstateWaterfallEdgeKind.Disposition) {
    const edgeValue = edge.value;

    const isEdgeToSelf =
      sourceNodeAttributes.data.id === targetNodeAttributes.data.id;

    // Check for other edges from this node in other sections
    const sectionDispositionHappens = waterfallEdgeFragments
      .filter(
        (e) =>
          e.from.id === edge.from.id &&
          (e.associatedDispositiveProvisions?.length ?? 0) > 0
      )
      .flatMap((e) => e.from.afterDeath)?.[0];

    const currentSection = edge.from.afterDeath;
    const entityHasDispositions = !!sectionDispositionHappens;
    const dispositionHappensInCurrentSection =
      currentSection === sectionDispositionHappens;

    // Extract individual provisions from the edge so the card can detect
    // and display asset-level DP details. Deduplicate by id because the
    // backend creates one associatedDispositiveProvisions entry per asset,
    // so a single DP with multiple assets appears multiple times.
    const associatedProvisions = uniqBy(
      compact(
        edge.associatedDispositiveProvisions?.map(
          (adp) => adp?.dispositiveProvision
        )
      ),
      'id'
    );

    const dispositionCard = createDispositionCard({
      source,
      target,
      edgeValue,
      sourceEntityName,
      targetEntityName,
      decedentName,
      isEdgeToSelf,
      entityHasDispositions,
      sourceIsTestamentaryEntity,
      sourceIsClientProfile,
      entityType,
      dispatch,
      sourceIsGroup: !!edge.from.group,
      targetIsGroup: !!edge.to.group,
      currentSection,
      dispositionHappensInCurrentSection,
      associatedProvisions,
    });

    if (dispositionCard) {
      cards.push(dispositionCard);
    }
  }

  // Add card for liabilities
  const possibleLiabilities = edge?.associatedTransferLogItems?.filter(
    (item) =>
      item.kind === EstateWaterfallTransferKind.LiabilityPaymentOnDeath &&
      item.associatedObject?.__typename === 'Liability'
  );

  possibleLiabilities?.forEach((liability) => {
    cards.push(
      createLiabilityCard({
        sourceEntityName,
        targetEntityName,
        liability,
      })
    );
  });

  // Add card for liability payments
  const possibleLiabilityPayments = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) =>
        item.kind === EstateWaterfallTransferKind.LiabilityPayment &&
        item.associatedObject?.__typename === 'Liability'
    ),
    (item) => item.associatedObjectID
  );

  Object.entries(possibleLiabilityPayments).forEach(
    ([_liabilityId, payments]) => {
      cards.push(
        createLiabilityCard({
          sourceEntityName,
          targetEntityName,
          payments,
        })
      );
    }
  );

  // CRT and CLT funding cards - from donor to trust
  const crtFundingItems = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) => item.kind === EstateWaterfallTransferKind.HypotheticalCrtFunding
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  const cltFundingItems = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) => item.kind === EstateWaterfallTransferKind.HypotheticalCltFunding
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(crtFundingItems).forEach(([_causeId, items]) => {
    if (items.length > 0) {
      const crtFundingCard = createCrtFundingCard({
        sourceEntityName,
        targetEntityName,
        transfer: items[0],
      });
      cards.push(crtFundingCard);
    }
  });

  Object.entries(cltFundingItems).forEach(([_causeId, items]) => {
    if (items.length > 0) {
      const cltFundingCard = createCltFundingCard({
        sourceEntityName,
        targetEntityName,
        transfer: items[0],
      });
      cards.push(cltFundingCard);
    }
  });

  // CRT income payments - from trust to income beneficiary
  const crtIncomePayments = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) => item.kind === EstateWaterfallTransferKind.CrtIncomePayment
    ) ?? [],
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(crtIncomePayments).forEach(([causeId, payments]) => {
    if (payments.length > 0) {
      const crtIncomeCard = createCrtIncomeCard({
        sourceEntityName,
        targetEntityName,
        incomePayments: payments,
        crtFunding: waterfallTransfers.find(
          (t) =>
            t.kind === EstateWaterfallTransferKind.HypotheticalCrtFunding &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(crtIncomeCard);
    }
  });

  // CRT remainder - from trust to charity
  const crtCharitableRemainder = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) => item.kind === EstateWaterfallTransferKind.CrtCharitableRemainder
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(crtCharitableRemainder).forEach(([causeId, items]) => {
    if (items.length > 0) {
      const crtRemainderCard = createCrtRemainderCard({
        sourceEntityName,
        targetEntityName,
        remainder: items[0],
        crtFunding: waterfallTransfers.find(
          (t) =>
            t.kind === EstateWaterfallTransferKind.HypotheticalCrtFunding &&
            t.associatedObjectID === causeId
        ),
        associatedIncomePayments: waterfallTransfers.filter(
          (t) =>
            t.kind === EstateWaterfallTransferKind.CrtIncomePayment &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(crtRemainderCard);
    }
  });

  // CLT income payments - from trust to charity
  const cltIncomePayments = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) => item.kind === EstateWaterfallTransferKind.CltIncomePayment
    ) ?? [],
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(cltIncomePayments).forEach(([causeId, payments]) => {
    if (payments.length > 0) {
      const cltIncomeCard = createCltIncomeCard({
        sourceEntityName,
        targetEntityName,
        incomePayments: payments,
        cltFunding: waterfallTransfers.find(
          (t) =>
            t.kind === EstateWaterfallTransferKind.HypotheticalCltFunding &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(cltIncomeCard);
    }
  });

  // CLT remainder - from trust to beneficiary
  const cltBeneficiaryRemainder = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) =>
        item.kind === EstateWaterfallTransferKind.CltBeneficiaryRemainder
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(cltBeneficiaryRemainder).forEach(([causeId, items]) => {
    if (items.length > 0) {
      const cltRemainderCard = createCltRemainderCard({
        sourceEntityName,
        targetEntityName,
        remainder: items[0],
        cltFunding: waterfallTransfers.find(
          (t) =>
            t.kind === EstateWaterfallTransferKind.HypotheticalCltFunding &&
            t.associatedObjectID === causeId
        ),
        associatedIncomePayments: waterfallTransfers.filter(
          (t) =>
            t.kind === EstateWaterfallTransferKind.CltIncomePayment &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(cltRemainderCard);
    }
  });

  // Add card for GRAT funding
  const gratFundingItems = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) =>
        item.kind === EstateWaterfallTransferKind.HypotheticalGratFunding
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(gratFundingItems).forEach(([causeId, items]) => {
    if (items.length > 0) {
      let directionCorrectedSourceEntityName = sourceEntityName;
      let directionCorrectedTargetEntityName = targetEntityName;

      if ((items[0] as { reversed?: boolean })?.reversed) {
        directionCorrectedSourceEntityName = targetEntityName;
        directionCorrectedTargetEntityName = sourceEntityName;
      }

      const gratFundingCard = createGratFundingCard({
        sourceEntityName: directionCorrectedSourceEntityName,
        targetEntityName: directionCorrectedTargetEntityName,
        transfer: items[0],
        associatedAnnuities: waterfallTransfers.filter(
          (t) =>
            t.kind === EstateWaterfallTransferKind.GratAnnuityPayment &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(gratFundingCard);
    }
  });

  // Add card for GRAT income
  const gratIncomeItems = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) => item.kind === EstateWaterfallTransferKind.GratAnnuityPayment
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(gratIncomeItems).forEach(([causeId, items]) => {
    if (items.length > 0) {
      let directionCorrectedSourceEntityName = sourceEntityName;
      let directionCorrectedTargetEntityName = targetEntityName;

      if ((items[0] as { reversed?: boolean })?.reversed) {
        directionCorrectedSourceEntityName = targetEntityName;
        directionCorrectedTargetEntityName = sourceEntityName;
      }

      const gratIncomeCard = createGratIncomeCard({
        sourceEntityName: directionCorrectedSourceEntityName,
        targetEntityName: directionCorrectedTargetEntityName,
        incomePayments: items,
        fundingTransfer: waterfallTransfers.find(
          (t) =>
            t.kind === EstateWaterfallTransferKind.HypotheticalGratFunding &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(gratIncomeCard);
    }
  });

  // Add card for GRAT remainder
  const gratRemainderItems = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) =>
        // Make sure the beneficiary payment is related to a GRAT since other
        // kinds can also have beneficiary payments
        item.kind === EstateWaterfallTransferKind.BeneficiaryPayment &&
        (item.associatedObject?.__typename === 'HypotheticalGRAT' ||
          (item.associatedObject?.__typename === 'Entity' &&
            item.associatedObject?.subtype?.__typename === 'GRATTrust'))
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(gratRemainderItems).forEach(([causeId, items]) => {
    if (items.length > 0) {
      let directionCorrectedSourceEntityName = sourceEntityName;
      let directionCorrectedTargetEntityName = targetEntityName;

      if ((items[0] as { reversed?: boolean })?.reversed) {
        directionCorrectedSourceEntityName = targetEntityName;
        directionCorrectedTargetEntityName = sourceEntityName;
      }

      const gratRemainderCard = createGratRemainderCard({
        sourceEntityName: directionCorrectedSourceEntityName,
        targetEntityName: directionCorrectedTargetEntityName,
        remainder: items[0],
        fundingTransfer: waterfallTransfers.find(
          (t) =>
            t.kind === EstateWaterfallTransferKind.HypotheticalGratFunding &&
            t.associatedObjectID === causeId
        ),
        associatedAnnuities: waterfallTransfers.filter(
          (t) =>
            t.kind === EstateWaterfallTransferKind.GratAnnuityPayment &&
            t.associatedObjectID === causeId
        ),
      });
      cards.push(gratRemainderCard);
    }
  });

  // Add card for QPRT remainder
  const qprtRemainderItems = groupBy(
    edge?.associatedTransferLogItems?.filter(
      (item) =>
        // Make sure the beneficiary payment is related to a QPRT
        item.kind === EstateWaterfallTransferKind.QprtBeneficiaryPayment
    ),
    (item) => item.associatedObjectID ?? UNKNOWN_CAUSE_ID
  );

  Object.entries(qprtRemainderItems).forEach(([_, items]) => {
    if (items.length > 0) {
      const qprtRemainderCard = createQprtRemainderCard({
        sourceEntityName,
        targetEntityName,
        remainder: items[0],
      });
      cards.push(qprtRemainderCard);
    }
  });

  return cards;
}
